import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Plus,
  Trash2,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { apiFetch, ApiError } from "@/lib/api-client";
import { useJournalEntry, type JournalEntryFormValues } from "@/features/journal-entries";
import { useAccounts, type Account } from "@/features/accounts";

// Line schema
const lineSchema = z.object({
  accountId: z.string().min(1, "Cuenta requerida"),
  direction: z.union([z.literal(1), z.literal(-1)]),
  amount: z.string().min(1, "Monto requerido").refine(
    (val) => !isNaN(parseFloat(val)) && parseFloat(val) > 0,
    "Monto debe ser mayor a 0"
  ),
  description: z.string().optional().nullable(),
});

// Form schema
const journalEntryFormSchema = z.object({
  transactionDate: z.string().min(1, "Fecha requerida"),
  description: z.string().min(1, "Descripcion requerida").max(500),
  reference: z.string().max(100).optional().nullable(),
  lines: z.array(lineSchema).min(2, "Minimo 2 lineas requeridas"),
});

type FormValues = z.infer<typeof journalEntryFormSchema>;

/**
 * Get today's date as ISO string (YYYY-MM-DD).
 */
function getToday(): string {
  return new Date().toISOString().split("T")[0] as string;
}

/**
 * Build flat list of selectable accounts (exclude header accounts).
 */
function buildAccountOptions(accounts: Account[]): { id: string; label: string }[] {
  // Sort by code
  const sorted = [...accounts].sort((a, b) => a.code.localeCompare(b.code));

  return sorted
    .filter((a) => !a.isHeader && a.isActive)
    .map((a) => ({
      id: a.id,
      label: `${a.code} - ${a.name}`,
    }));
}

/**
 * Format number as currency for display.
 */
function formatAmount(value: string): string {
  const num = parseFloat(value);
  if (isNaN(num)) return "0.00";
  return new Intl.NumberFormat("es-CL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

/**
 * Full-page form for creating/editing journal entries.
 */
export function JournalEntryFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNewEntry = id === "nuevo";
  const entryId = isNewEntry ? undefined : id;

  // Fetch existing entry if editing
  const {
    entry,
    isLoading: isLoadingEntry,
    error: entryError,
  } = useJournalEntry(entryId);

  // Fetch accounts for selector
  const { accounts, isLoading: isLoadingAccounts } = useAccounts();

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Build account options
  const accountOptions = useMemo(
    () => buildAccountOptions(accounts),
    [accounts]
  );

  // Form setup
  const form = useForm<FormValues>({
    resolver: zodResolver(journalEntryFormSchema),
    defaultValues: {
      transactionDate: getToday(),
      description: "",
      reference: "",
      lines: [
        { accountId: "", direction: 1, amount: "", description: "" },
        { accountId: "", direction: -1, amount: "", description: "" },
      ],
    },
  });

  // Field array for lines
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "lines",
  });

  // Populate form when entry loads (edit mode)
  useEffect(() => {
    if (entry && !isNewEntry) {
      form.reset({
        transactionDate: entry.transactionDate,
        description: entry.description,
        reference: entry.reference ?? "",
        lines: entry.lines.map((line) => ({
          accountId: line.accountId,
          direction: line.direction as 1 | -1,
          amount: line.functionalAmount,
          description: line.description ?? "",
        })),
      });
    }
  }, [entry, isNewEntry, form]);

  // Watch lines for balance calculation - useWatch triggers re-renders on changes
  const watchedLines = useWatch({
    control: form.control,
    name: "lines",
  });

  // Calculate totals
  const totals = useMemo(() => {
    let debit = 0;
    let credit = 0;

    if (watchedLines) {
      for (const line of watchedLines) {
        const amount = parseFloat(line.amount) || 0;
        if (line.direction === 1) {
          debit += amount;
        } else {
          credit += amount;
        }
      }
    }

    const difference = Math.abs(debit - credit);
    const isBalanced = difference < 0.01;

    return { debit, credit, difference, isBalanced };
  }, [watchedLines]);

  // Check if form is read-only (POSTED or REVERSED entries)
  const isReadOnly = entry ? entry.status !== "DRAFT" : false;

  // Handle form submission
  const handleSubmit = async (values: FormValues) => {
    if (!totals.isBalanced) {
      toast.error("El asiento debe estar balanceado antes de guardar");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload: JournalEntryFormValues = {
        transactionDate: values.transactionDate,
        description: values.description,
        reference: values.reference || null,
        lines: values.lines.map((line) => ({
          accountId: line.accountId,
          direction: line.direction,
          amount: line.amount,
          description: line.description || null,
        })),
      };

      if (isNewEntry) {
        await apiFetch.post("/api/journal-entries", payload);
        toast.success("Asiento creado correctamente");
      } else {
        await apiFetch.put(`/api/journal-entries/${entryId}`, payload);
        toast.success("Asiento actualizado correctamente");
      }

      navigate("/contabilidad/diario");
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message);
      } else {
        toast.error("Error al guardar el asiento");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle posting
  const handlePost = async () => {
    if (!entryId || !totals.isBalanced) return;

    setIsSubmitting(true);

    try {
      await apiFetch.post(`/api/journal-entries/${entryId}/post`, {});
      toast.success("Asiento contabilizado correctamente");
      navigate("/contabilidad/diario");
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message);
      } else {
        toast.error("Error al contabilizar el asiento");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add new line
  const addLine = (direction: 1 | -1) => {
    append({ accountId: "", direction, amount: "", description: "" });
  };

  // Loading state
  if (isLoadingEntry || isLoadingAccounts) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state
  if (entryError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-2 text-destructive">
        <AlertCircle className="h-8 w-8" />
        <p>Error al cargar el asiento</p>
        <p className="text-sm text-muted-foreground">{entryError.message}</p>
        <Button variant="outline" onClick={() => navigate("/contabilidad/diario")}>
          Volver al Diario
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/contabilidad/diario")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold">
            {isNewEntry
              ? "Nuevo Asiento"
              : `Asiento #${String(entry?.entryNumber ?? 0).padStart(6, "0")}`}
          </h1>
          {entry && (
            <p className="text-sm text-muted-foreground">
              Estado:{" "}
              <span
                className={cn(
                  "font-medium",
                  entry.status === "DRAFT" && "text-yellow-600",
                  entry.status === "POSTED" && "text-green-600",
                  entry.status === "REVERSED" && "text-red-600"
                )}
              >
                {entry.status === "DRAFT" && "Borrador"}
                {entry.status === "POSTED" && "Contabilizado"}
                {entry.status === "REVERSED" && "Anulado"}
              </span>
            </p>
          )}
        </div>
      </div>

      {/* Read-only notice */}
      {isReadOnly && (
        <div className="bg-muted p-4 rounded-lg flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600" />
          <p className="text-sm">
            Este asiento esta contabilizado y no puede editarse.
          </p>
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          {/* Header fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="transactionDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fecha</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} disabled={isReadOnly} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reference"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Referencia</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      placeholder="Factura #, Cheque #, etc."
                      disabled={isReadOnly}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="md:col-span-1">
                  <FormLabel>Descripcion</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Descripcion del asiento"
                      disabled={isReadOnly}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Lines table */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-muted px-4 py-2 flex items-center justify-between">
              <h3 className="font-medium">Lineas</h3>
              {!isReadOnly && (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addLine(1)}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Debito
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addLine(-1)}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Credito
                  </Button>
                </div>
              )}
            </div>

            <table className="w-full">
              <thead>
                <tr className="border-b text-left text-sm text-muted-foreground bg-muted/50">
                  <th className="px-4 py-2 font-medium">#</th>
                  <th className="px-4 py-2 font-medium">Cuenta</th>
                  <th className="px-4 py-2 font-medium">Descripcion</th>
                  <th className="px-4 py-2 font-medium text-right">Debito</th>
                  <th className="px-4 py-2 font-medium text-right">Credito</th>
                  {!isReadOnly && <th className="px-4 py-2 w-10"></th>}
                </tr>
              </thead>
              <tbody>
                {fields.map((field, index) => (
                  <tr key={field.id} className="border-b last:border-0">
                    <td className="px-4 py-2 text-sm text-muted-foreground">
                      {index + 1}
                    </td>
                    <td className="px-4 py-2">
                      <FormField
                        control={form.control}
                        name={`lines.${index}.accountId`}
                        render={({ field: accountField }) => (
                          <FormItem className="space-y-0">
                            <Select
                              onValueChange={accountField.onChange}
                              value={accountField.value}
                              disabled={isReadOnly}
                            >
                              <FormControl>
                                <SelectTrigger className="w-full min-w-[250px]">
                                  <SelectValue placeholder="Seleccione cuenta" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {accountOptions.map((opt) => (
                                  <SelectItem
                                    key={opt.id}
                                    value={opt.id}
                                    className="font-mono text-sm"
                                  >
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <FormField
                        control={form.control}
                        name={`lines.${index}.description`}
                        render={({ field: descField }) => (
                          <FormItem className="space-y-0">
                            <FormControl>
                              <Input
                                {...descField}
                                value={descField.value ?? ""}
                                placeholder="Detalle (opcional)"
                                disabled={isReadOnly}
                                className="min-w-[150px]"
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </td>
                    <td className="px-4 py-2">
                      {watchedLines?.[index]?.direction === 1 ? (
                        <FormField
                          control={form.control}
                          name={`lines.${index}.amount`}
                          render={({ field: amountField }) => (
                            <FormItem className="space-y-0">
                              <FormControl>
                                <Input
                                  {...amountField}
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="0.00"
                                  disabled={isReadOnly}
                                  className="text-right font-mono w-28"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      ) : (
                        <div className="w-28 h-9 bg-muted rounded-md" />
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {watchedLines?.[index]?.direction === -1 ? (
                        <FormField
                          control={form.control}
                          name={`lines.${index}.amount`}
                          render={({ field: amountField }) => (
                            <FormItem className="space-y-0">
                              <FormControl>
                                <Input
                                  {...amountField}
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="0.00"
                                  disabled={isReadOnly}
                                  className="text-right font-mono w-28"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      ) : (
                        <div className="w-28 h-9 bg-muted rounded-md" />
                      )}
                    </td>
                    {!isReadOnly && (
                      <td className="px-4 py-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => remove(index)}
                          disabled={fields.length <= 2}
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t bg-muted/50">
                  <td colSpan={3} className="px-4 py-2 text-right font-medium">
                    Totales:
                  </td>
                  <td className="px-4 py-2 text-right font-mono font-medium">
                    {formatAmount(totals.debit.toString())}
                  </td>
                  <td className="px-4 py-2 text-right font-mono font-medium">
                    {formatAmount(totals.credit.toString())}
                  </td>
                  {!isReadOnly && <td></td>}
                </tr>
                <tr className="bg-muted/50">
                  <td colSpan={3} className="px-4 py-2 text-right">
                    Diferencia:
                  </td>
                  <td
                    colSpan={isReadOnly ? 2 : 3}
                    className={cn(
                      "px-4 py-2 text-right font-mono",
                      totals.isBalanced
                        ? "text-green-600"
                        : "text-red-600 font-medium"
                    )}
                  >
                    {totals.isBalanced ? (
                      <span className="inline-flex items-center gap-1">
                        <CheckCircle className="h-4 w-4" />
                        Cuadra
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        <AlertTriangle className="h-4 w-4" />
                        {formatAmount(totals.difference.toString())}
                      </span>
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Form errors */}
          {form.formState.errors.lines && (
            <p className="text-sm text-destructive">
              {form.formState.errors.lines.message ||
                form.formState.errors.lines.root?.message}
            </p>
          )}

          {/* Actions */}
          {!isReadOnly && (
            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/contabilidad/diario")}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  "Guardar Borrador"
                )}
              </Button>
              {!isNewEntry && entry?.status === "DRAFT" && (
                <Button
                  type="button"
                  variant="default"
                  onClick={handlePost}
                  disabled={isSubmitting || !totals.isBalanced}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Contabilizando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Contabilizar
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </form>
      </Form>
    </div>
  );
}
